import { Events, emit } from './events.js';

class MarqueeScroller extends HTMLElement {
  connectedCallback() {
    try {
      const host = this;
      // Pause handling
      this.__onEnter = () => this.#setPaused(true);
      this.__onLeave = () => this.#setPaused(false);
      host.addEventListener('pointerenter', this.__onEnter, false);
      host.addEventListener('pointerleave', this.__onLeave, false);

      // Respect author opt-out via data-pause="false"
      this.__pauseEnabled = (host.getAttribute('data-pause') || 'true') !== 'false';

      // Ensure content fills at least the container width by cloning items
      this.__ensureFilled = () => {
        try {
          const track = host.querySelector('.store-marquee__track');
          const left = track ? track.querySelector('.store-marquee__inner') : null;
          if (!track || !left) return;
          const containerWidth = host.clientWidth || 0;
          const firstChild = left.firstElementChild;
          const firstChildWidth = firstChild ? (firstChild.getBoundingClientRect().width || 0) : 0;
          // Require strong overfill so the seam is always outside the viewport
          // (2x container width prevents blanks on ultra-wide screens and after loops)
          const requiredWidth = Math.max(containerWidth * 2, containerWidth + firstChildWidth);
          // If left inner is already wider than required, nothing to do
          if (left.scrollWidth >= requiredWidth) return;
          // Clone children until we exceed container width
          const children = Array.from(left.children);
          if (children.length === 0) return;
          let guard = 0;
          while (left.scrollWidth < requiredWidth && guard < 1000) {
            for (const child of children) {
              left.appendChild(child.cloneNode(true));
              if (left.scrollWidth >= requiredWidth) break;
            }
            guard++;
          }
          // Mirror left into the aria-hidden right inner to keep seamless loop
          const right = track.querySelector('.store-marquee__inner[aria-hidden="true"]');
          if (right) {
            right.innerHTML = '';
            for (const child of left.children) {
              right.appendChild(child.cloneNode(true));
            }
          }
        } catch (_) {}
      };

      // Compute duration from px/s speed and content width
      this.__computeAndApply = () => {
        try {
          const speedAttr = host.getAttribute('data-speed');
          const rawSpeed = Number(speedAttr ?? 60);
          // If speed is 0 or invalid, pause the animation without twitching
          if (!isFinite(rawSpeed) || rawSpeed <= 0) {
            host.setAttribute('data-stopped', '');
            host.style.setProperty('--marquee-duration', '0s');
            return;
          } else {
            host.removeAttribute('data-stopped');
          }
          const inner = host.querySelector('.store-marquee__inner');
          if (!inner) return;
          // Use scrollWidth to account for overflow content
          const innerWidth = inner.scrollWidth;
          if (!innerWidth || !isFinite(innerWidth)) return;
          const durationSec = innerWidth / rawSpeed;
          host.style.setProperty('--marquee-duration', `${durationSec}s`);
        } catch (_) {}
      };

      // Initial compute on next frame so layout is ready
      this.__raf = requestAnimationFrame(() => { this.__ensureFilled(); this.__computeAndApply(); });

      // Recompute on resize/layout changes
      this.__onWindowResize = () => { this.__ensureFilled(); this.__computeAndApply(); };
      window.addEventListener('resize', this.__onWindowResize, { passive: true });
      // Observe inner size changes
      const inner = host.querySelector('.store-marquee__inner');
      if (inner && 'ResizeObserver' in window) {
        this.__ro = new ResizeObserver(() => { this.__ensureFilled(); this.__computeAndApply(); });
        this.__ro.observe(inner);
      }
    } catch (_) {}
  }
  disconnectedCallback() {
    try {
      this.removeEventListener('pointerenter', this.__onEnter, false);
      this.removeEventListener('pointerleave', this.__onLeave, false);
      if (this.__onWindowResize) window.removeEventListener('resize', this.__onWindowResize, { passive: true });
      if (this.__ro) {
        try { this.__ro.disconnect(); } catch (_) {}
        this.__ro = null;
      }
      if (this.__raf) cancelAnimationFrame(this.__raf);
    } catch (_) {}
  }
  #setPaused(paused) {
    if (!this.__pauseEnabled) return;
    this.toggleAttribute('data-paused', !!paused);
  }
}

try { if (!customElements.get('marquee-scroller')) customElements.define('marquee-scroller', MarqueeScroller); } catch (_) {}


